import React, { useState, useRef, useEffect } from 'react';
import { FaCheck, FaTimes } from 'react-icons/fa';
import DatePicker from 'react-datepicker';

type BookingDropProps = {
    fetchBookings: ({
        statuses,
        createdDateFrom,
        createdDateTo
    }: {
        statuses?: string[];
        createdDateFrom?: Date;
        createdDateTo?: Date;
    }) => void;
};

export function BookingDrop({ fetchBookings }: BookingDropProps) {
    const [selectedType, setSelectedType] = useState<string[]>([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Date filter states
    const [createdDateFrom, setCreatedDateFrom] = useState<Date | undefined>(undefined);
    const [createdDateTo, setCreatedDateTo] = useState<Date | undefined>(undefined);

    const options = [
        { value: 'sea', label: 'Sea' },
        { value: 'land', label: 'Land' },
        { value: 'air', label: 'Air' },
    ];

    // Clear selected statuses and apply filters
    const handleClearStatus = () => {
        setSelectedType([]);
        setCreatedDateFrom(undefined);
        setCreatedDateTo(undefined);
        fetchBookings({
            statuses: [],
            createdDateFrom: undefined,
            createdDateTo: undefined,
        });
    };

    // Apply filters based on selected statuses and date ranges
    const handleApplyFilters = () => {
        const createdDateFromUTC = createdDateFrom ? new Date(createdDateFrom.getTime() - createdDateFrom.getTimezoneOffset() * 60000) : undefined;
        const createdDateToUTC = createdDateTo ? new Date(createdDateTo.getTime() - createdDateTo.getTimezoneOffset() * 60000) : undefined;

        fetchBookings({
            statuses: selectedType,
            createdDateFrom: createdDateFromUTC,
            createdDateTo: createdDateToUTC,
        });
    };

    // Toggle dropdown for status selection
    const handleStatusChange = (value: string) => {
        setSelectedType((prev) =>
            prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
        );
    };

    // Remove selected status
    const handleRemoveStatus = (value: string) => {
        setSelectedType((prev) => prev.filter((item) => item !== value));
    };

    // Toggle dropdown visibility
    const toggleDropdown = (e: React.MouseEvent) => {
        e.stopPropagation();
        setDropdownOpen((prev) => !prev);
    };

    // Close dropdown when clicking outside
    const closeDropdown = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setDropdownOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', closeDropdown);
        return () => {
            document.removeEventListener('click', closeDropdown);
        };
    }, []);

    return (
        <div className='menu menu-sub menu-sub-dropdown w-250px w-md-300px' data-kt-menu='true' ref={dropdownRef}>
            <div className='px-7 py-5'>
                <div className='fs-5 text-dark fw-bolder'>Filter Options</div>
            </div>

            <div className='separator border-gray-200'></div>

            <div className='px-7 py-5'>
                {/* Status Filter */}
                <div className="mb-3" onClick={(e) => e.stopPropagation()}>
                    <label className="form-label fw-bold">Shipment Mode:</label>
                    <div className="custom-select-container position-relative" onClick={toggleDropdown}>
                        <div className="border rounded py-3 px-2" style={{ cursor: 'pointer', backgroundColor: '#F9F9F9' }}>
                            {selectedType.length > 0 ? (
                                selectedType.map((status) => (
                                    <span key={status} className="badge bg-primary me-1">
                                        {options.find((option) => option.value === status)?.label}
                                        <FaTimes
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveStatus(status);
                                            }}
                                            className="ms-1"
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </span>
                                ))
                            ) : (
                                <span className="text-muted">Select option</span>
                            )}
                        </div>
                        {dropdownOpen && (
                            <ul className="list-group position-absolute mt-1 w-100" style={{ zIndex: '1' }}>
                                {options.map((option) => (
                                    <li
                                        key={option.value}
                                        className={`list-group-item d-flex justify-content-between align-items-center border-0 ${selectedType.includes(option.value) ? 'bg-light text-primary' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleStatusChange(option.value);
                                        }}
                                    >
                                        {option.label}
                                        {selectedType.includes(option.value) && <FaCheck />}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Created Date Filter */}
                <div className="mb-3">
                    <label className="form-label fw-bold">Created Date:</label>
                    <div className="d-flex align-items-center">
                        <div onClick={(e: React.MouseEvent<HTMLInputElement, MouseEvent>) => e.stopPropagation()}>
                            <DatePicker
                                selected={createdDateFrom}
                                onChange={(date) => setCreatedDateFrom(date ? date : undefined)}
                                selectsStart
                                startDate={createdDateFrom}
                                endDate={createdDateTo}
                                maxDate={new Date()}  
                                className="form-control"
                                placeholderText="From"
                                dateFormat="dd/MM/yyyy"
                            />
                        </div>
                        
                        <span className="mx-2">to</span>
                        <div onClick={(e: React.MouseEvent<HTMLInputElement, MouseEvent>) => e.stopPropagation()}>
                            <DatePicker
                                selected={createdDateTo}
                                onChange={(date) => setCreatedDateTo(date ? date : undefined)}
                                selectsEnd
                                startDate={createdDateFrom}
                                endDate={createdDateTo}
                                minDate={createdDateFrom}
                                maxDate={new Date()}  
                                className="form-control"
                                placeholderText="To"
                                dateFormat="dd/MM/yyyy"
                            />
                        </div>
                    </div>
                </div>

                <div className='d-flex justify-content-end'>
                    <button
                        type='reset'
                        className='btn btn-sm btn-white btn-active-light-primary me-2'
                        data-kt-menu-dismiss='true'
                        onClick={handleClearStatus}
                    >
                        Reset
                    </button>

                    <button
                        type='submit'
                        className='btn btn-sm btn-primary'
                        data-kt-menu-dismiss='true'
                        onClick={handleApplyFilters}
                    >
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
}
